import React, { useState, useEffect, useContext, useCallback, Fragment } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { getDateFromOffset } from '../../../helpers/dates';
import { WelletContext } from '../../../context/WelletContext';
import { skeletonLoading } from '../../../components/Skeletons/SkeletonAccount';
import { useTranslation } from 'react-i18next';
import { changeCurrency } from '../../../actions/settingsActions';
import ContentApp from '../../../components/ContentApp';
import Balance from '../../../components/Balance/Balance';
import EmptyActivity from '../../../components/Common/EmptyActivity/EmptyActivity';
import AccountHeader from '../../../components/AccountHeader/AccountHeader';
import WarningBox from '../../../components/Common/WarningBox/WarningBox';
import ActivityReservationItem from '../../../components/Activities/ActivityReservationItem.js/ActivityReservationItem';
import useRealtimeReservations from '../../../hooks/useRealtimeReservations';
import { CircleSpinner } from 'react-spinners-kit';
import AccountFilterIcon from '../../../components/icons/AccountFilterIcon';
import CustomButton from '../../../components/ButtomCustom/CustomButton';
import ArrowIcon from '../../../components/icons/ArrowIcon';
import InstagramIcon2 from '../../../components/icons/InstagramIcon2';
import QrIconHome from '../../../components/icons/QrIconHome';
import IconReserva from '../../../components/icons/IconReserva';
import NavSelector from '../../../components/NavSelector/NavSelector';
import PaymentLink from '../../../components/icons/PaymentLink';
import WithdrawalMethodWarningBox from '../../../components/Common/WithdrawalMethodWarningBox/WithdrawalMethodWarningBox';
import { ScanQrCode, ScanBarcode } from 'lucide-react';

const Account = ({ history }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const welletContext = useContext(WelletContext);
  const jwt = useSelector((state) => state.associates.jwt);
  const user = useSelector((state) => state.associates.user);
  const today = getDateFromOffset(user.companyUtcMinutesOffset);
  const todayFilter = format(today, 'yyyy-MM-dd');
  const [loadingBalance, setLoadingBalance] = useState(true);
  const [filterDate, setFilterDate] = useState(
    localStorage.getItem('filterDate') ?? 'RESERVATION_DATE',
  );
  const [myEarningsToday, setMyEarningsToday] = useState(null);
  const [monthEarning, setMonthEarning] = useState(0);
  const kycStatus = useSelector((state) => state.associates.user.kycStatus);
  const [selectedOption, setSelectedOption] = useState(filterDate);
  const advantageNetworkStatus = useSelector(
    (state) => state?.associates?.user?.advantageNetworkStatus,
  );
  const app = useSelector((state) => state.app);
  const currency = useSelector((state) => state.currency.default);

  const getMyEarningsToday = async () => {
    setLoadingBalance(true);
    try {
      const balance = await welletContext.apis.tickets.get(
        `/associates/GetEarnings?from=${todayFilter}&to=${todayFilter}`,
      );
      setMyEarningsToday(balance.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingBalance(false);
    }
  };

  const getEarnings = async (fromDate, toDate) => {
    setLoadingBalance(true);
    try {
      const balance = await welletContext.apis.tickets.get(
        `/associates/GetEarnings?from=${fromDate}&to=${toDate}`,
      );
      setMonthEarning(balance.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingBalance(false);
    }
  };

  const handleValidReservationUpdate = useCallback(() => {
    getMyEarningsToday();

    const date = getDateFromOffset(user.companyUtcMinutesOffset);
    const firstMonthDate = format(startOfMonth(date), 'yyyy-MM-dd');
    const lastMonthDate = format(endOfMonth(date), 'yyyy-MM-dd');

    getEarnings(firstMonthDate, lastMonthDate);
  }, [getMyEarningsToday, getEarnings]);

  const reservationTypes = {
    ReservationTypeInclude: [],
    ReservationTypeExclude: ['LINK'],
  };

  const { reservations, loading, hasMore, loadingMore, fetchMoreReservations } =
    useRealtimeReservations(today, filterDate, handleValidReservationUpdate, reservationTypes);

  useEffect(() => {
    getMyEarningsToday();
    const date = getDateFromOffset(user.companyUtcMinutesOffset);
    const firstMonthDate = format(startOfMonth(date), 'yyyy-MM-dd');
    const lastMonthDate = format(endOfMonth(date), 'yyyy-MM-dd');

    getEarnings(firstMonthDate, lastMonthDate);
  }, [jwt]);

  useEffect(() => {
    const storedFilterDate = localStorage.getItem('filterDate');
    if (storedFilterDate) {
      setFilterDate(storedFilterDate);
    }
  }, []);

  const isEmptyActivity = reservations?.length === 0 || undefined;

  useEffect(() => {
    if (currency) {
      dispatch(changeCurrency(currency));
    }
  }, [currency]);

  useEffect(() => {
    if (!localStorage.getItem('filterDate')) localStorage.setItem('filterDate', 'RESERVATION_DATE');
  }, []);

  const handleSelectChange = (selectedOption) => {
    localStorage.setItem('filterDate', selectedOption);
    setFilterDate(selectedOption);
    setSelectedOption(selectedOption);
  };

  const renderButtonsLayout = () => {
    const buttons = [];

    const pushButton = (key, element, isReservation = false) => {
      buttons.push({ key, element, isReservation });
    };

    pushButton(
      'campaigns',
      <CustomButton
        size='link'
        text={t('campaigns')}
        leftIcon={<InstagramIcon2 size={20} fillColor='#000' />}
        rightIcon={<ArrowIcon size={15} />}
        onClick={() => history.push('/campaigns-feed')}
      />,
    );

    pushButton(
      'qr',
      <CustomButton
        size='link'
        rightIcon={<ArrowIcon size={15} />}
        leftIcon={<QrIconHome size={20} fillColor='#000' />}
        text={t('QR')}
        onClick={() => history.push('/qr-restaurant')}
      />,
    );

    if (user?.roles?.includes('doorman')) {
      pushButton(
        'checkin',
        <CustomButton
          size='link'
          rightIcon={<ArrowIcon size={15} />}
          leftIcon={<ScanBarcode size={24} fillColor='#000' />}
          text={t('accessControl')}
          onClick={() => history.push('/check-in')}
        />,
      );
    }

    if (user?.roles?.includes('link')) {
      pushButton(
        'paymentLink',
        <CustomButton
          size='link'
          rightIcon={<ArrowIcon size={15} />}
          leftIcon={<PaymentLink size={25} fillColor='#000' />}
          text={t('paymentLink')}
          onClick={() => history.push('/link-home')}
        />,
      );
    }

    const reservationButton = {
      key: 'reservation',
      isReservation: true,
      element: (
        <CustomButton
          size={buttons.length % 2 === 0 ? 'reserva' : 'link'}
          className='font-normal'
          fontSize='1.3rem'
          rightIcon={<ArrowIcon size={15} />}
          leftIcon={<IconReserva size={25} fillColor='#000' />}
          text={t('makeNewReservation')}
          onClick={() => history.push('/restaurant-list')}
        />
      ),
    };

    pushButton(reservationButton.key, reservationButton.element, true);

    const total = buttons.length;
    const isEven = total % 2 === 0;

    const normalButtons = buttons.filter((b) => !b.isReservation);
    const reservation = buttons.find((b) => b.isReservation);

    let leftColumn = [];
    let rightColumn = [];

    if (isEven) {
      const half = total / 2;
      leftColumn = buttons.slice(0, half);
      rightColumn = buttons.slice(half);
    } else {
      const leftCount = Math.ceil(total / 2);

      leftColumn = normalButtons.slice(0, leftCount);
      rightColumn = [reservation, ...normalButtons.slice(leftCount)];
    }

    return (
      <>
        <div className='d-flex flex-column' style={{ width: '50%', gap: '0.5rem' }}>
          {leftColumn.map((b) => (
            <Fragment key={b.key}>{b.element}</Fragment>
          ))}
        </div>

        <div className='w-50 d-flex flex-column' style={{ width: '50%', gap: '0.5rem' }}>
          {rightColumn.map((b) => (
            <Fragment key={b.key}>{b.element}</Fragment>
          ))}
        </div>
      </>
    );
  };

  return (
    <>
      <AccountHeader title={app.name} icon={'config'} pathName='/configuration' />
      <ContentApp menu header>
        {advantageNetworkStatus !== 'APPROVED' ? (
          <div className='mb-2'>
            <WarningBox
              title={t('advantageNetworkStatus.title')}
              subtitle={t('advantageNetworkStatus.subtitle')}
            />
          </div>
        ) : (
          kycStatus === 'REJECTED' && (
            <div className='mb-2'>
              <WarningBox
                title={t('identityNotValidated.title')}
                subtitle={t('identityNotValidated.subtitle')}
                textLink={t('identityNotValidated.textLink')}
                onClickText={() => history.push('/kyc-start')}
              />
            </div>
          )
        )}

        <WithdrawalMethodWarningBox />
        <div className='mt-2'>
          <Balance
            title={t('earnings')}
            todayBalance={myEarningsToday?.amount}
            last7DaysBalance={monthEarning?.amount}
            showText={true}
            fontSize='1.8rem'
            showAllBalances={true}
            loading={loadingBalance}
            mpVerified={true}
            showArrow={true}
          />
        </div>

        <div className='d-flex mt-2' style={{ gap: '0.5rem' }}>
          {renderButtonsLayout()}
        </div>

        <div className='bold font-normal text-lighter-gray mt-3'>{t('myReservations')}</div>
        <div className='row mt-3 mb-4'>
          <div className='col-auto pr-0'>
            <div className='account-button' onClick={() => history.push('/all-reservations')}>
              <AccountFilterIcon color='#7E7E7E' size='16px' styles={{ marginTop: '-2px' }} />
            </div>
          </div>
          <div className='col-auto px-1'>
            <div
              className={`account-button ${
                selectedOption === 'RESERVATION_DATE' ? 'selected' : ''
              }`}
              onClick={() => handleSelectChange('RESERVATION_DATE')}
            >
              {t('forToday')}
            </div>
          </div>
          <div className='col-auto col-md p-0'>
            <div
              className={`account-button ${selectedOption === 'CREATED_DATE' ? 'selected' : ''}`}
              onClick={() => handleSelectChange('CREATED_DATE')}
            >
              {t('mostRecent')}
            </div>
          </div>
          <div className='col pl-1'>
            <div
              className={`account-button ${
                selectedOption === 'RECENT_COMMISSIONS' ? 'selected' : ''
              }`}
              onClick={() => handleSelectChange('RECENT_COMMISSIONS')}
            >
              {t('lastCommissions')}
            </div>
          </div>
        </div>

        {loading ? (
          skeletonLoading()
        ) : (
          <>
            {isEmptyActivity ? (
              <EmptyActivity subtitle={<div className='bold'>{t('noBookings')}</div>} />
            ) : (
              <>
                {reservations?.map((r, index) => (
                  <div key={index}>
                    <ActivityReservationItem
                      activity={r}
                      date={r?.reservationLocalTime}
                      rightText={r.amount}
                      index={index}
                      lastIndex={reservations.length - 1}
                      handleClick={() => history.push(`/operation-details?id=${r.id}`)}
                    />
                  </div>
                ))}
                {loadingMore ? (
                  <div className='p-3 d-flex justify-content-center'>
                    <CircleSpinner
                      className='spinner-app mx-auto'
                      style={{ margin: 'auto' }}
                      size={40}
                      color='var(--color-font-primary)'
                    />
                  </div>
                ) : (
                  hasMore && (
                    <div
                      className='text-secondary font-small fw-semibold text-center p-2 mt-2'
                      style={{ border: '1px solid #3D3D3D', borderRadius: '5px' }}
                      onClick={fetchMoreReservations}
                    >
                      {t('showMore')}
                    </div>
                  )
                )}
              </>
            )}
          </>
        )}

        <NavSelector history={history} />
      </ContentApp>
    </>
  );
};

export default Account;
